import type { QuestionData, QuestionSerialized } from '@repo/types/questions/Question';
import type { IQuestion } from '@repo/types/questions/Question.interface';
import type { IQuestionFactory } from '@repo/types/questions/QuestionFactory.interface';
import type { AnswerUserQuestionFill, FeedbackAnswerQuestionFill, QuestionFillData } from '@repo/types/questions/QuestionFill';
import type { AnswerUserQuestionLong, FeedbackAnswerQuestionLong, QuestionLongData } from '@repo/types/questions/QuestionLong';
import type { AnswerUserQuestionMatching, FeedbackAnswerQuestionMatching, QuestionMatchingData } from '@repo/types/questions/QuestionMatching';
import type { AnswerUserQuestionMultiple, FeedbackAnswerQuestionMultiple, QuestionMultipleData } from '@repo/types/questions/QuestionMultiple';
import type { AnswerUserQuestionSingle, FeedbackAnswerQuestionSingle, QuestionSingleData } from '@repo/types/questions/QuestionSingle';
import type { AnswerUserQuestionTranslate, FeedbackAnswerQuestionTranslate, QuestionTranslateData } from '@repo/types/questions/QuestionTranslate';
import type { AnswerUserQuestionVoice, FeedbackAnswerQuestionVoice, QuestionVoiceData } from '@repo/types/questions/QuestionVoice';

import { QuestionType } from '@repo/types/enums/questionType';

import { QuestionFill } from './QuestionFill';
import { QuestionLong } from './QuestionLong';
import { QuestionMatching } from './QuestionMatching';
import { QuestionMultiple } from './QuestionMultiple';
import { QuestionSingle } from './QuestionSingle';
import { QuestionTranslate } from './QuestionTranslate';
import { QuestionVoice } from './QuestionVoice';

export class QuestionFactory implements IQuestionFactory {
  public createQuestion(_data: QuestionData, _selectedAnswer: QuestionSerialized['selectedAnswer'] | null = null, _marked: QuestionSerialized['marked'] | null = null): IQuestion {
    const type: QuestionType = _data.type;

    switch (type) {
      case QuestionType.SINGLE: {
        const data = _data as QuestionSingleData;
        const selectedAnswer = _selectedAnswer as AnswerUserQuestionSingle;
        const marked = _marked as FeedbackAnswerQuestionSingle;
        return new QuestionSingle(data, selectedAnswer, marked);
      }
      case QuestionType.MULTIPLE: {
        const data = _data as QuestionMultipleData;
        const selectedAnswer = _selectedAnswer as AnswerUserQuestionMultiple | null;
        const marked = _marked as FeedbackAnswerQuestionMultiple | null;
        return new QuestionMultiple(data, selectedAnswer, marked);
      }
      case QuestionType.FILL: {
        const data = _data as QuestionFillData;
        const selectedAnswer = _selectedAnswer as AnswerUserQuestionFill | null;
        const marked = _marked as FeedbackAnswerQuestionFill | null;
        return new QuestionFill(data, selectedAnswer, marked);
      }
      case QuestionType.MATCHING: {
        const data = _data as QuestionMatchingData;
        const selectedAnswer = _selectedAnswer as AnswerUserQuestionMatching | null;
        const marked = _marked as FeedbackAnswerQuestionMatching | null;
        return new QuestionMatching(data, selectedAnswer, marked);
      }
      case QuestionType.TRANSLATE: {
        const data = _data as QuestionTranslateData;
        const selectedAnswer = _selectedAnswer as AnswerUserQuestionTranslate | null;
        const marked = _marked as FeedbackAnswerQuestionTranslate | null;
        return new QuestionTranslate(data, selectedAnswer, marked);
      }
      case QuestionType.LONG: {
        const data = _data as QuestionLongData;
        const selectedAnswer = _selectedAnswer as AnswerUserQuestionLong | null;
        const marked = _marked as FeedbackAnswerQuestionLong | null;
        return new QuestionLong(data, selectedAnswer, marked);
      }
      case QuestionType.VOICE: {
        const data = _data as QuestionVoiceData;
        const selectedAnswer = _selectedAnswer as AnswerUserQuestionVoice | null;
        const marked = _marked as FeedbackAnswerQuestionVoice | null;
        return new QuestionVoice(data, selectedAnswer, marked);
      }
      default:
        throw new Error('Unknown question type');
    }
  }
}
